import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button } from '../shared';
import { getForecast } from '../../utils/api';
import { AGENTS } from '../../config';
import './ForecastResults.css';

function ForecastResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRound, setSelectedRound] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState('gerry');

  useEffect(() => {
    const loadForecast = async () => {
      try {
        const data = await getForecast(id);
        console.log('Forecast data:', data);

        // Normalize data structure - handle both complete (rounds object) and
        // in-progress (flat structure) responses
        if (!data.rounds && (data.round_1_outputs || data.round_1)) {
          // Build rounds object from flat response for consistency
          data.rounds = {
            round_1: data.round_1_outputs ? { agent_outputs: data.round_1_outputs } : data.round_1,
            round_2: data.round_2_outputs ? { agent_outputs: data.round_2_outputs } : data.round_2,
            round_3: data.round_3_outputs ? { agent_outputs: data.round_3_outputs } : data.round_3,
            mediator_1: data.mediator_summary_1 || data.mediator_1,
            mediator_2: data.mediator_summary_2 || data.mediator_2,
            search: data.qa_pairs ? { qa_pairs: data.qa_pairs } : data.search,
          };
        }

        setForecast(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadForecast();
  }, [id]);

  const renderRoundComparison = () => {
    if (!forecast?.rounds) return null;

    const rounds = [1, 2, 3];
    const roundLabels = ['Round 1', 'Round 2', 'Round 3'];

    return (
      <Card className="comparison-card">
        <h2 className="section-title">Forecast Progression</h2>
        <div className="comparison-table">
          <div className="comparison-header">
            <div className="comparison-cell agent-header">Agent</div>
            {roundLabels.map(label => (
              <div key={label} className="comparison-cell round-header">{label}</div>
            ))}
          </div>
          {Object.entries(AGENTS).map(([agentId, agent]) => (
            <div key={agentId} className="comparison-row">
              <div className="comparison-cell agent-cell">
                <span
                  className="agent-dot"
                  style={{ backgroundColor: agent.color }}
                />
                <span className="agent-name">{agent.name}</span>
              </div>
              {rounds.map(roundNum => {
                const roundKey = `round_${roundNum}`;
                const roundData = forecast.rounds?.[roundKey];
                const agentOutput = roundData?.agent_outputs?.find(o => o.agent_id === agentId);
                return (
                  <div key={roundKey} className="comparison-cell forecast-cell">
                    {agentOutput?.forecast ? (
                      <>
                        <span className="forecast-mean">
                          {(agentOutput.forecast.mean * 100).toFixed(1)}%
                        </span>
                        <span className="forecast-std text-tertiary">
                          ±{(agentOutput.forecast.std_dev * 100).toFixed(1)}%
                        </span>
                      </>
                    ) : (
                      <span className="text-tertiary">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderAgentDetails = () => {
    if (!forecast?.rounds) return null;

    const roundKey = `round_${selectedRound}`;
    const roundData = forecast.rounds?.[roundKey];
    const agentOutput = roundData?.agent_outputs?.find(o => o.agent_id === selectedAgent);
    const agent = AGENTS[selectedAgent];

    return (
      <Card className="agent-details-card">
        <h2 className="section-title">Agent Outputs</h2>

        {/* Round Tabs */}
        <div className="tabs-container">
          <div className="tabs round-tabs">
            {[1, 2, 3].map(roundNum => (
              <button
                key={roundNum}
                className={`tab ${selectedRound === roundNum ? 'tab--active' : ''}`}
                onClick={() => setSelectedRound(roundNum)}
              >
                Round {roundNum}
              </button>
            ))}
          </div>
        </div>

        {/* Agent Tabs */}
        <div className="tabs-container">
          <div className="tabs agent-tabs">
            {Object.entries(AGENTS).map(([agentId, agentInfo]) => (
              <button
                key={agentId}
                className={`tab tab--agent ${selectedAgent === agentId ? 'tab--active' : ''}`}
                onClick={() => setSelectedAgent(agentId)}
                style={{
                  '--agent-color': agentInfo.color,
                }}
              >
                <span
                  className="agent-dot"
                  style={{ backgroundColor: agentInfo.color }}
                />
                {agentInfo.name}
              </button>
            ))}
          </div>
        </div>

        {/* Agent Output Content */}
        {agentOutput ? (
          <div className="agent-output">
            <div className="agent-output-header">
              <div className="agent-info">
                <span
                  className="agent-dot-large"
                  style={{ backgroundColor: agent.color }}
                />
                <div>
                  <h3 className="agent-output-name">{agent.name}</h3>
                  <span className="agent-output-model text-tertiary">{agent.model}</span>
                </div>
              </div>
              <div className="agent-output-forecast">
                <span className="agent-forecast-value">
                  {(agentOutput.forecast.mean * 100).toFixed(1)}%
                </span>
                <span className="agent-forecast-std text-tertiary">
                  ± {(agentOutput.forecast.std_dev * 100).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="agent-output-section">
              <h4 className="agent-output-label">Reasoning</h4>
              <p className="agent-output-reasoning">{agentOutput.reasoning}</p>
            </div>

            {agentOutput.sources_cited?.length > 0 && (
              <div className="agent-output-section">
                <h4 className="agent-output-label">Sources Cited</h4>
                <div className="agent-sources-list">
                  {agentOutput.sources_cited
                    .sort((a, b) => (b.importance_score || 0) - (a.importance_score || 0))
                    .map((source, idx) => (
                      <div key={idx} className="agent-source-item">
                        <div className="agent-source-header">
                          <span className="agent-source-id">{source.source_id}</span>
                          <span className="agent-source-score">
                            Importance: {source.importance_score}/10
                          </span>
                        </div>
                        {source.excerpt && (
                          <p className="agent-source-excerpt">{source.excerpt}</p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="agent-output-empty">
            <p className="text-tertiary">No data available for this round/agent combination.</p>
          </div>
        )}
      </Card>
    );
  };

  const renderMediatorSummaries = () => {
    const mediator1 = forecast?.rounds?.mediator_1;
    const mediator2 = forecast?.rounds?.mediator_2;

    if (!mediator1 && !mediator2) return null;

    return (
      <Card className="mediator-section">
        <h2 className="section-title">Mediator Summaries</h2>

        {mediator1 && (
          <div className="mediator-block">
            <h3 className="mediator-round">After Round 1</h3>
            <p className="mediator-text">{mediator1.summary}</p>
          </div>
        )}

        {mediator2 && (
          <div className="mediator-block">
            <h3 className="mediator-round">After Round 2</h3>
            <p className="mediator-text">{mediator2.summary}</p>
          </div>
        )}
      </Card>
    );
  };

  const renderSources = () => {
    const sources = forecast?.information_values || [];
    const qaPairs = forecast?.rounds?.search?.qa_pairs || {};

    if (sources.length === 0) return null;

    const sortedSources = [...sources].sort((a, b) => b.weighted_value - a.weighted_value);
    const maxValue = sortedSources[0]?.weighted_value || 1;

    return (
      <Card className="sources-section">
        <h2 className="section-title">Information Sources</h2>
        <p className="section-subtitle text-secondary">
          Ranked by importance based on citation frequency and round weighting
        </p>

        <div className="sources-list">
          {sortedSources.map((source, index) => {
            const qaData = qaPairs[source.source_id];
            return (
              <div key={source.source_id} className="source-item source-item--expandable">
                <div className="source-rank">#{index + 1}</div>
                <div className="source-content">
                  <div className="source-header">
                    <span className="source-id">{source.source_id}</span>
                    <span className="source-citations text-tertiary">
                      {source.citation_count} citations
                    </span>
                  </div>
                  <div className="source-bar-container">
                    <div
                      className="source-bar"
                      style={{ width: `${(source.weighted_value / maxValue) * 100}%` }}
                    />
                  </div>
                  <div className="source-meta">
                    <span className="source-value font-mono">
                      Score: {source.weighted_value.toFixed(3)}
                    </span>
                    {source.average_raw_score && (
                      <span className="source-avg text-tertiary font-mono">
                        Avg: {source.average_raw_score.toFixed(1)}/10
                      </span>
                    )}
                  </div>
                  {qaData && (
                    <div className="source-qa">
                      <div className="source-question">
                        <strong>Q:</strong> {qaData.question}
                      </div>
                      <div className="source-answer">
                        <strong>A:</strong> {qaData.answer}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="forecast-results-page">
        <div className="forecast-results-container">
          <p className="text-center text-secondary">Loading forecast...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="forecast-results-page">
        <div className="forecast-results-container">
          <Card className="error-card">
            <h2>Error</h2>
            <p>{error}</p>
            <Button onClick={() => navigate('/')} variant="secondary">
              Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="forecast-results-page">
      <div className="forecast-results-container">
        <header className="results-header">
          <Link to="/" className="back-link">← New Forecast</Link>
          <h1 className="results-title">{forecast?.market_title}</h1>
        </header>

        <Card className="final-result-card" variant="elevated">
          <div className="final-result">
            <div className="final-label">Final Forecast</div>
            <div className="final-forecast">
              <span className="final-mean">
                {forecast?.final_forecast
                  ? `${(forecast.final_forecast.mean * 100).toFixed(1)}%`
                  : '—'}
              </span>
              {forecast?.final_forecast && (
                <span className="final-std text-secondary">
                  ± {(forecast.final_forecast.std_dev * 100).toFixed(1)}%
                </span>
              )}
            </div>
            {forecast?.final_forecast?.confidence_interval_90 && (
              <div className="confidence-interval text-tertiary">
                90% CI: [{(forecast.final_forecast.confidence_interval_90[0] * 100).toFixed(1)}% - {(forecast.final_forecast.confidence_interval_90[1] * 100).toFixed(1)}%]
              </div>
            )}
          </div>
        </Card>

        {renderRoundComparison()}
        {renderAgentDetails()}
        {renderMediatorSummaries()}
        {renderSources()}
      </div>
    </div>
  );
}

export default ForecastResults;
